import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ClimbersDealWorkDetail.css';

const imageBase = '/images/這是我的鞋子project的圖片';

const showcaseSlides = [
  {
    eyebrow: 'Browse',
    title: 'A Deal Finder for Climbing Gear',
    image: `${imageBase}/Simulator Screenshot - 6.5寸螢幕11promax - 2025-09-10 at 23.04.41.png`,
    body: 'Climbers Deal is a mobile app designed for climbers who want to find better prices on climbing gear, especially climbing shoes. Instead of checking many store websites one by one, users can browse available products, compare offers, and see where the same item is being sold at a lower price — all in one place.',
  },
  {
    eyebrow: 'Save',
    title: 'Save Time and Money on Expensive Gear',
    image: `${imageBase}/Simulator Screenshot - 6.5寸螢幕11promax - 2025-09-10 at 23.06.51.png`,
    body: 'Climbing gear can be expensive, and prices often change across different retailers. The app collects product and price information from multiple stores and presents it in one place, making it easier for climbers to decide when and where to buy. The main purpose of the app is to help users save both time and money.',
  },
  {
    eyebrow: 'Compare',
    title: 'One Product, All the Offers',
    image: `${imageBase}/Simulator Screenshot - 6.5寸螢幕11promax - 2025-09-10 at 23.08.10.png`,
    body: 'Users can browse products, open a product detail page, and view different offers for the same model. For example, if a climbing shoe is sold by several shops, the app shows the available prices so users can compare them quickly without jumping between store websites.',
  },
  {
    eyebrow: 'Track',
    title: 'Track Prices, Get Notified When They Drop',
    image: `${imageBase}/Simulator Screenshot - 6.5寸螢幕11promax - 2025-09-10 at 23.08.01.png`,
    body: 'When users find a product they are interested in, they can choose to track it. If the price drops later, the app sends them a push notification, so they do not need to manually check the price every day. It works like a deal finder and price alert tool for climbers in one mobile experience.',
  },
  {
    eyebrow: 'Choose',
    title: 'Pick the Right Shoe for Your Climbing',
    image: `${imageBase}/Screenshot 2025-11-10 at 6.52.42 PM.png`,
    body: 'Another useful feature is the climbing shoe comparison section. It helps users compare different shoe models based on characteristics such as stiffness, aggressiveness, and suitability for different climbing styles. Useful not only for finding discounts, but also for choosing the right type of shoe depending on the climber\u2019s experience level and climbing needs — great for both experienced climbers waiting for the best price and beginners who want help comparing shoes.',
  },
];

function RevealBlock({ children, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.18 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`cdw-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function ClimbersDealWorkDetail() {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const slide = showcaseSlides[activeSlide];
  const totalSlides = showcaseSlides.length;

  // Scroll-driven hero animation refs
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const iconRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const icon = iconRef.current;
    if (!hero || !title || !subtitle || !icon) return undefined;

    // Map a value into a sub-range, clamped to 0-1
    const sub = (p, start, end) =>
      Math.max(0, Math.min(1, (p - start) / (end - start)));
    // Smooth ease-out for a softer feel
    const ease = (t) => 1 - Math.pow(1 - t, 2);

    let frame = 0;

    const update = () => {
      // sticky 軌道：用「在 hero 內已經滾過多少」算 progress
      // hero 比 viewport 高，多出來的高度就是動畫可用的滾動距離
      const heroHeight = hero.offsetHeight || window.innerHeight;
      const viewportH = window.innerHeight;
      const scrollRange = Math.max(1, heroHeight - viewportH);
      const heroTop = hero.getBoundingClientRect().top + window.scrollY;
      const scrolledInside = window.scrollY - heroTop;
      const progress = Math.max(0, Math.min(1, scrolledInside / scrollRange));

      const titleP = ease(sub(progress, 0, 0.55));
      const subtitleP = ease(sub(progress, 0.18, 0.7));
      const iconP = ease(sub(progress, 0.05, 0.95));

      title.style.opacity = String(titleP);
      title.style.transform = `scale(${0.55 + 0.45 * titleP})`;

      subtitle.style.opacity = String(subtitleP);
      subtitle.style.transform = `scale(${0.5 + 0.5 * subtitleP})`;

      // 移動距離以 icon 本身寬度為基準，避免手機把 icon 推出畫面太遠
      const isMobile = viewportH > 0 && window.innerWidth < 680;
      const iconW = icon.offsetWidth || 200;
      const travel = Math.min(iconW * 1.4, window.innerWidth * 0.62);
      const iconX = -travel * (1 - iconP);
      const iconRot = (isMobile ? -42 : -78) * (1 - iconP);
      const iconScale = 0.86 + 0.14 * iconP;
      icon.style.opacity = String(iconP);
      icon.style.transform = `translateX(${iconX}px) rotateY(${iconRot}deg) scale(${iconScale})`;
      icon.style.filter = `blur(${(1 - iconP) * 5}px)`;
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  const goPrevious = () => {
    setActiveSlide((current) =>
      current === 0 ? totalSlides - 1 : current - 1
    );
  };

  const goNext = () => {
    setActiveSlide((current) =>
      current === totalSlides - 1 ? 0 : current + 1
    );
  };

  return (
    <div className="cdw-page">
      <button className="cdw-back" onClick={() => navigate('/my-work')}>
        ← My Work
      </button>

      <section className="cdw-hero" ref={heroRef}>
        <div className="cdw-hero-stage">
          <div className="cdw-glow cdw-glow-one" />
          <div className="cdw-glow cdw-glow-two" />

          <RevealBlock className="cdw-hero-grid">
            <div className="cdw-hero-copy">
              <span className="cdw-kicker">iOS App · Climbing Gear Deals</span>
              <h1 ref={titleRef} className="cdw-hero-title">
                Climbers Deal
              </h1>
              <p className="cdw-hero-subtitle" ref={subtitleRef}>
                Why I am making this?
              </p>
              <p>
                It is an iOS app inspired by climbing. I love climbing, and
                climbing shoes are essential gear. Shoes are made by rubber, so
                they wear out quickly. Because climbing is still a niche sport,
                good shoes can be hard to get when they go on sale. Discounts
                disappear quickly, so I wanted an app that helps me know right
                away when a product becomes cheaper.
              </p>
              <p>
                That idea became Climbers Deal: a mobile app for finding better
                prices on climbing gear, especially climbing shoes.
              </p>
              <div className="cdw-tech-row">
                {['React Native', 'Expo', 'Node.js', 'PostgreSQL'].map(
                  (tech) => (
                    <span key={tech}>{tech}</span>
                  )
                )}
              </div>
            </div>

            <div className="cdw-icon-stage">
              <div className="cdw-icon-orbit" />
              <img
                ref={iconRef}
                src={`${imageBase}/icon origin.png`}
                alt="Climbers Deal app icon"
                className="cdw-app-icon"
              />
            </div>
          </RevealBlock>
        </div>
      </section>

      <section className="cdw-story">
        <RevealBlock className="cdw-story-header">
          <span className="cdw-kicker">Product Flow</span>
          <h2>Browse, compare, track, and get notified.</h2>
          <p>
            The app works like a deal finder and price alert tool for climbers.
            It brings together product information, price comparison, and
            notifications in one mobile experience.
          </p>
        </RevealBlock>

        <RevealBlock className="cdw-showcase">
          <div className="cdw-phone-panel">
            <div className="cdw-phone-frame">
              <img src={slide.image} alt={slide.title} />
            </div>
          </div>

          <div className="cdw-slide-copy">
            <span className="cdw-slide-count">
              {String(activeSlide + 1).padStart(2, '0')} /{' '}
              {String(totalSlides).padStart(2, '0')}
            </span>
            <span className="cdw-slide-eyebrow">{slide.eyebrow}</span>
            <h3>{slide.title}</h3>
            <p>{slide.body}</p>

            <div className="cdw-controls">
              <button onClick={goPrevious} aria-label="Previous slide">
                ←
              </button>
              <div className="cdw-dots">
                {showcaseSlides.map((item, index) => (
                  <button
                    key={item.title}
                    className={index === activeSlide ? 'active' : ''}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Go to ${item.title}`}
                  />
                ))}
              </div>
              <button onClick={goNext} aria-label="Next slide">
                →
              </button>
            </div>
          </div>
        </RevealBlock>
      </section>

      <section className="cdw-build">
        <RevealBlock className="cdw-build-card">
          <span className="cdw-kicker">Technical Notes</span>
          <h2>Built as a real cross-platform product.</h2>
          <p>
            From a technical point of view, the app is built with{' '}
            <strong>React Native</strong> and <strong>Expo</strong>, which means
            it can run as a mobile app on both iOS and Android. The backend uses{' '}
            <strong>Node.js</strong>, <strong>Express</strong>, and a{' '}
            <strong>PostgreSQL</strong> database to store products, offers,
            price history, and tracking information.
          </p>
          <p>
            Product and price data are collected through automated web scraping
            tools such as <strong>Puppeteer</strong> and{' '}
            <strong>Cheerio</strong>. The app also uses{' '}
            <strong>push notifications</strong> to alert users about price
            drops, <strong>React Query</strong> and local storage to improve
            loading and caching, and <strong>Sentry</strong> to monitor errors
            in production.
          </p>

          <div className="cdw-stack-grid">
            <div>
              <span>Mobile</span>
              <p>React Native · Expo · iOS &amp; Android</p>
            </div>
            <div>
              <span>Backend</span>
              <p>Node.js · Express · PostgreSQL</p>
            </div>
            <div>
              <span>Data Pipeline</span>
              <p>Puppeteer · Cheerio · Scheduled scraping</p>
            </div>
            <div>
              <span>App Layer</span>
              <p>Push Notifications · React Query · Local Storage · Sentry</p>
            </div>
          </div>
        </RevealBlock>
      </section>
    </div>
  );
}
